import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text, useDisclosure } from '@chakra-ui/react';
import { MdToken } from 'react-icons/md';

import './components/Home.css';
import TopBar from './components/TopBar';
import Controls from './components/Controls';
import Transmission from './components/Transmission';
import RaceWorld from './game/RaceWorld';
import { cars, colors, pedals } from './Models';

const clampIndex = (value, list) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed < list.length ? parsed : 0;
};

const detectQuality = () => {
  const cores = navigator.hardwareConcurrency || 4;
  const memory = navigator.deviceMemory || 4;
  const compact = Math.min(window.innerWidth, window.innerHeight) < 390;
  return cores <= 4 || memory <= 4 || compact ? 'low' : 'high';
};

const Home = () => {
  const [activeCar, setActiveCar] = useState(() => clampIndex(localStorage.getItem('activeCar'), cars));
  const [activeColors, setActiveColors] = useState(() => clampIndex(localStorage.getItem('activeColors'), colors));
  const [activePedals, setActivePedals] = useState(() => clampIndex(localStorage.getItem('activePedals'), pedals));
  //const [pedalsIsAuto, setPedalsIsAuto] = useState(() => JSON.parse(localStorage.getItem('pedalsIsAuto') || 'false'));
  const [pedalsIsAuto, setPedalsIsAuto] = useState(true);
  const [tokens, setTokens] = useState(() => Number(localStorage.getItem('tokens') || 0));
  const [allTokens, setAllTokens] = useState(() => Number(localStorage.getItem('allTokens') || 0));
  const [speedNum, setSpeedNum] = useState(0);
  const [isAccelerating, setIsAccelerating] = useState(false);
  const [isMovingLeft, setIsMovingLeft] = useState(false);
  const [isMovingRight, setIsMovingRight] = useState(false);
  const [laneTarget, setLaneTarget] = useState(1);
  const [impact, setImpact] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [category, setCategory] = useState(0);
  const [quality] = useState(detectQuality);

  const { isOpen: GarageIsOpen, onOpen: GarageOnOpen, onClose: GarageOnClose } = useDisclosure();
  const { isOpen: InfoIsOpen, onOpen: InfoOnOpen, onClose: InfoOnClose } = useDisclosure();

  useEffect(() => {
    if (!localStorage.getItem('newUser') && !InfoIsOpen) InfoOnOpen();

    if (cars[activeCar]?.price > 0 && localStorage.getItem(`myCar${activeCar}`) !== 'true') setActiveCar(0);
    if (pedals[activePedals]?.price > 0 && localStorage.getItem(`myPedal${activePedals}`) !== 'true') setActivePedals(0);
    if (colors[activeColors]?.price > 0 && localStorage.getItem(`myColor${activeColors}`) !== 'true') setActiveColors(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { localStorage.setItem('activeCar', String(activeCar)); }, [activeCar]);
  useEffect(() => { localStorage.setItem('activeColors', String(activeColors)); }, [activeColors]);
  useEffect(() => { localStorage.setItem('activePedals', String(activePedals)); }, [activePedals]);
  useEffect(() => { localStorage.setItem('pedalsIsAuto', JSON.stringify(pedalsIsAuto)); }, [pedalsIsAuto]);

  useEffect(() => {
    if (isMovingLeft) {
      if (isAccelerating) {
        setLaneTarget((v) => Math.max(0, v - 1));
      }

      setIsMovingLeft(false);
    }
  }, [isMovingLeft, isAccelerating]);

  useEffect(() => {
    if (isMovingRight) {
      if (isAccelerating) {
        setLaneTarget((v) =>
          Math.max(0, Math.min(2, v + 1))
        );
      }

      setIsMovingRight(false);
    }
  }, [isMovingRight, isAccelerating]);

  useEffect(() => {
    const down = (e) => {
      const key = e.key.toLowerCase();

      if (
        isAccelerating &&
        (key === 'a' || key === 'arrowleft' || key === 'ф')
      ) {
        setLaneTarget((v) => {
          const current = Number.isFinite(v) ? v : 1;

          return Math.max(
            0,
            Math.min(2, current - 1)
          );
        });
      }

      if (
        isAccelerating &&
        (key === 'd' || key === 'arrowright' || key === 'в')
      ) {
        setLaneTarget((v) => {
          const current = Number.isFinite(v) ? v : 1;

          return Math.max(
            0,
            Math.min(2, current + 1)
          );
        });
      }

      if (
        (key === 'w' ||
          key === 'arrowup' ||
          key === 'ц') &&
        !pedalsIsAuto
      ) {
        setIsAccelerating(true);
      }

      if (key === ' ' && pedalsIsAuto) {
        setIsAccelerating((v) => !v);
      }
    };

    const up = (e) => {
      const key = e.key.toLowerCase();

      if (
        (key === 'w' ||
          key === 'arrowup' ||
          key === 'ц') &&
        !pedalsIsAuto
      ) {
        setIsAccelerating(false);
      }
    };

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);

    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [pedalsIsAuto, isAccelerating]);

  const addToken = useCallback((amount = 1) => {
    setTokens((prev) => {
      const next = prev + amount;
      localStorage.setItem('tokens', String(next));
      return next;
    });

    setAllTokens((prev) => {
      const next = prev + amount;
      localStorage.setItem('allTokens', String(next));
      return next;
    });

    setImpact({
      type: 'plus',
      amount,
      rare: amount >= 10,
      id: Date.now(),
    });
  }, []);

  const hitEnemy = useCallback(() => {
    setTokens((prev) => {
      const next = Math.max(0, prev - 15);
      localStorage.setItem('tokens', String(next));
      return next;
    });
    setImpact({ type: 'minus', id: Date.now() });
  }, []);

  useEffect(() => {
    if (!impact) return undefined;
    const timer = setTimeout(
      () => setImpact(null),
      impact.rare ? 900 : 680
    );
    return () => clearTimeout(timer);
  }, [impact]);

  const currentCar = cars[activeCar] || cars[0];
  const raceCar = useMemo(() => ({
    ...currentCar,
    raceScale: currentCar.raceScale || currentCar.scale,
  }), [currentCar]);

  return (
    <Box className="darian-race-shell">
      <Box className={`race-stage ${impact?.type === 'minus' ? 'race-impact' : ''}`} opacity={viewMode ? 0.12 : 1}>
        <RaceWorld
          running={isAccelerating && !GarageIsOpen && !InfoIsOpen}
          laneTarget={laneTarget}
          activeCar={raceCar}
          activeColor={colors[activeColors]?.color || '#c41111'}
          speedKmh={speedNum}
          onSpeed={setSpeedNum}
          onToken={addToken}
          onCrash={hitEnemy}
          quality={quality}
        />
      </Box>

      <TopBar
        speedNum={speedNum}
        tokens={tokens}
        allTokens={allTokens}
        setTokens={setTokens}
        cars={cars}
        setViewMode={setViewMode}
        InfoIsOpen={InfoIsOpen}
        InfoOnOpen={InfoOnOpen}
        InfoOnClose={InfoOnClose}
        activeCar={activeCar}
        setActiveCar={setActiveCar}
        colors={colors}
        activeColors={activeColors}
        setActiveColors={setActiveColors}
        pedals={pedals}
        pedalsIsAuto={pedalsIsAuto}
        setIsAccelerating={setIsAccelerating}
        isAccelerating={isAccelerating}
        setPedalsIsAuto={setPedalsIsAuto}
        activePedals={activePedals}
        setActivePedals={setActivePedals}
        category={category}
        setCategory={setCategory}
        GarageIsOpen={GarageIsOpen}
        GarageOnOpen={GarageOnOpen}
        GarageOnClose={GarageOnClose}
      />

      {!viewMode && <Transmission speedNum={speedNum} maxSource={currentCar.maxSpeed} />}

      {impact && (
        <Box
          key={impact.id}
          className={`
            race-score-pop
            race-score-pop-${impact.type}
            ${impact.rare ? 'race-score-pop-rare' : ''}
          `}
        >
          {impact.type === 'plus'
            ? `+${impact.amount || 1}`
            : '-15'
          } <MdToken />
        </Box>
      )}

      <Box className="race-lane-hint" aria-hidden="true">
        {Array.from({ length: 3 }).map((_, index) => (
          <i
            key={index}
            className={laneTarget === index ? 'active' : ''}
          />
        ))}
      </Box>

      <Controls
        GarageIsOpen={GarageIsOpen}
        GarageOnOpen={GarageOnOpen}
        GarageOnClose={GarageOnClose}
        pedals={pedals[activePedals]}
        setIsMovingLeft={setIsMovingLeft}
        setIsMovingRight={setIsMovingRight}
        pedalsIsAuto={pedalsIsAuto}
        isAccelerating={isAccelerating}
        setIsAccelerating={setIsAccelerating}
        category={category}
      />
    </Box>
  );
};

export default Home;
